import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';

import api from '.';
import { ConfirmPaymentRequestDto } from '../dtos/requests/confirm-payment.request.dto';
import { PreparePaymentRequestDto } from '../dtos/requests/prepare-payment.request.dto';
import { RequestRefundRequestDto } from '../dtos/requests/request-refund.request.dto';
import { BaseResponseDto } from '../dtos/responses/base.response.dto';
import { ConfirmPaymentResponseDto } from '../dtos/responses/confirm-payment.response.dto';
import { EmptyResponseDto } from '../dtos/responses/empty.response.dto';
import { GetMyPaymentsResponseDto } from '../dtos/responses/get-my-payments.response.dto';
import { PreparePaymentResponseDto } from '../dtos/responses/prepare-payment.response.dto';

// 유료 강좌 결제 주문 생성(READY). 프론트는 이 orderId/amount로 Toss 결제창을 연다.
const preparePayment = (
  data: PreparePaymentRequestDto,
): Promise<BaseResponseDto<PreparePaymentResponseDto>> => {
  return api<BaseResponseDto<PreparePaymentResponseDto>, PreparePaymentRequestDto>(
    `/student/payment/prepare`,
    {
      method: 'POST',
      data,
    },
  );
};

export const usePreparePayment = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<PreparePaymentResponseDto>, unknown, PreparePaymentRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<PreparePaymentResponseDto>, unknown, PreparePaymentRequestDto>({
    mutationFn: preparePayment,
    ...options,
  });
};

// Toss 결제 성공 후 서버 승인 검증 + 수강 등록.
const confirmPayment = (
  data: ConfirmPaymentRequestDto,
): Promise<BaseResponseDto<ConfirmPaymentResponseDto>> => {
  return api<BaseResponseDto<ConfirmPaymentResponseDto>, ConfirmPaymentRequestDto>(
    `/student/payment/confirm`,
    {
      method: 'POST',
      data,
    },
  );
};

export const useConfirmPayment = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<ConfirmPaymentResponseDto>, unknown, ConfirmPaymentRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<ConfirmPaymentResponseDto>, unknown, ConfirmPaymentRequestDto>({
    mutationFn: confirmPayment,
    ...options,
  });
};

// 내 결제/환불 내역 (결제 금액 포함).
const getMyPayments = () => {
  return api<BaseResponseDto<GetMyPaymentsResponseDto[]>, Error>(`/student/payment`, {
    method: 'GET',
  });
};

export const useGetMyPayments = (
  options?: Omit<UseQueryOptions<BaseResponseDto<GetMyPaymentsResponseDto[]>, Error>, 'queryFn'>,
) => {
  return useQuery({
    queryKey: ['student', 'payment', 'my'],
    queryFn: () => getMyPayments(),
    ...options,
  });
};

// 환불 신청(사유 필수). 관리자 승인 대기 상태로 접수된다.
const requestRefund = (
  data: RequestRefundRequestDto,
): Promise<BaseResponseDto<EmptyResponseDto>> => {
  return api<BaseResponseDto<EmptyResponseDto>, RequestRefundRequestDto>(`/student/payment/refund`, {
    method: 'POST',
    data,
  });
};

export const useRequestRefund = (
  options?: Omit<
    UseMutationOptions<BaseResponseDto<EmptyResponseDto>, unknown, RequestRefundRequestDto>,
    'mutationFn'
  >,
) => {
  return useMutation<BaseResponseDto<EmptyResponseDto>, unknown, RequestRefundRequestDto>({
    mutationFn: requestRefund,
    ...options,
  });
};
